import { ChangeEvent, useEffect, useState } from 'react'
import { HStack, Input, Radio, RadioGroup, Stack, Text } from '@chakra-ui/react'

import CustomFormFieldErrorMessage from 'components/tools/CustomForm/commonComponents/CustomFormFieldErrorMessage'
import { CustomFormViewerQuestionProps } from 'components/tools/CustomForm/types/CustomFormViewerQuestionProps'
import validateSingleSelectionBoxOnCustomForm from 'components/tools/CustomForm/components/Viewer/utils/validateSingleSelectionBoxOnCustomForm'
import { useCustomFormViewer } from 'components/tools/CustomForm/components/Viewer/contexts/CustomFormViewerManager/Provider'
import useCustomFormGetDefaultValue from '../../hooks/useCustomFormGetDefaultValue'
import useCustomFormHandleErrorWithScrollBehavior from '../../hooks/useCustomFormHandleErrorWithScrollBehavior'

export default function CustomFormSingleSelectionBoxQuestion({
  question,
  questionKey,
  formMethods,
  disabled,
}: CustomFormViewerQuestionProps) {
  const [otherOptionValue, setOtherOptionValue] = useState<string>(' ')
  const [enableOtherField, setEnableOtherField] = useState<boolean>(false)

  const { removeErrorFromQuestion, setErrorOnQuestion, thisQuestionHasError } = useCustomFormViewer()

  const { defaultValue } = useCustomFormGetDefaultValue({ question, questionKey, disabled })

  const questionWithError = thisQuestionHasError(question.slug)?.question

  const hasLongOption = question.options?.some((option) => option.length > 20) ?? false

  const hasManyOptions = question && question.options && question.options?.length >= 2

  const hasToBeColumns = hasLongOption || hasManyOptions || question?.options?.includes('Outro')

  function handleOnChangeInput(event: ChangeEvent<HTMLInputElement>) {
    const inputValue = event.target.value
    formMethods?.setValue(questionKey, inputValue)
    setOtherOptionValue(inputValue)
  }

  function handleEnableOtherField() {
    setEnableOtherField(!enableOtherField)
  }

  function removeOtherField() {
    setEnableOtherField(false)
  }

  const initialValue = defaultValue || (formMethods?.watch && formMethods.watch(questionKey)) || ''

  useEffect(() => {
    if (formMethods && formMethods.setValue) {
      if (question.answer) {
        formMethods.setValue(questionKey, question.answer[0])
      } else {
        formMethods.setValue(questionKey, initialValue)
      }
    }
  }, [initialValue, question])

  useEffect(() => {
    if (initialValue) {
      const errorMessage = validateSingleSelectionBoxOnCustomForm({
        option: initialValue,
        isRequired: question.is_required,
      })
      if (errorMessage === '') {
        if (formMethods?.setValue) {
          formMethods.setValue(questionKey, initialValue)
        }
        removeErrorFromQuestion(question.slug)
      } else {
        setErrorOnQuestion(question, errorMessage)
      }
    }
  }, [initialValue])

  useCustomFormHandleErrorWithScrollBehavior({ questionWithError, questionSlug: question.slug })

  return (
    <Stack id={question.slug}>
      <RadioGroup
        size="md"
        value={enableOtherField ? otherOptionValue : formMethods?.watch ? undefined : initialValue}
        defaultValue={initialValue}
      >
        <Stack direction={hasToBeColumns ? 'column' : 'row'} spacing="6px">
          {question?.options?.map((option) => {
            if (option === 'Outro') {
              return (
                <HStack key={option}>
                  <Radio
                    alignItems={hasLongOption ? 'start' : 'center'}
                    isDisabled={disabled}
                    value={otherOptionValue}
                    onClick={handleEnableOtherField}
                    {...(formMethods?.register && formMethods.register(questionKey))}
                  />

                  <Text marginTop={hasToBeColumns ? '-2px' : '0px'}>{option}</Text>

                  <Input
                    autoComplete="off"
                    value={otherOptionValue}
                    onChange={handleOnChangeInput}
                    isDisabled={disabled}
                    rounded="none"
                    bg="transparent"
                    border="none"
                    borderBottom="2px"
                    color="dark"
                  />
                </HStack>
              )
            }
            return (
              <HStack key={option}>
                <Radio
                  onClick={removeOtherField}
                  alignItems={hasToBeColumns ? 'start' : 'center'}
                  isDisabled={disabled}
                  value={option}
                  {...(formMethods?.register && formMethods.register(questionKey))}
                />

                <Text marginTop={hasToBeColumns ? '-2px' : '0px'}>{option}</Text>
              </HStack>
            )
          })}
        </Stack>
      </RadioGroup>

      <CustomFormFieldErrorMessage message={questionWithError?.error_message} />
    </Stack>
  )
}
